import { Checkbox, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { SmartForm, SmartSubmit, useSmartForm } from "@nodeon/react-components";
import React, { useCallback, useMemo } from "react";
import { SubmitHandler, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  AccessEnum,
  AccessItemDto,
  MapAreaItemDto,
  PermissionItemDto,
  useAccessApi,
  useMapAreaApi,
} from "~/api/GeneratedApi";
import LoadingBox from "~/components/LoadingBox";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";

interface FormProps {
  permissions: PermissionItemDto[];
}

interface Props {
  permissions: PermissionItemDto[];
  onSubmit: SubmitHandler<FormProps>;
}

type AccessPair = {
  read: AccessItemDto;
  update: AccessItemDto;
  name: string;
};

export const PermissionEdit = ({
  permissions,
  onSubmit,
}: Props): JSX.Element => {
  const { t } = useTranslation("permission-edit");
  const mapAreaApi = useMapAreaApi();
  const accessApi = useAccessApi();

  const defaultValues = useMemo(() => ({ permissions }), [permissions]);

  const formMethods = useSmartForm<FormProps>({
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: formMethods.control,
    name: "permissions",
  });

  React.useEffect(() => {
    fields.forEach((_, i) =>
      formMethods.register(
        `permissions.${i}.accessId` as `permissions.${number}.accessId`,
      ),
    );
  }, [fields, formMethods]);

  const { isLoading: isLoadingAcesses, data: accesses } = useCustomQuery(
    "accesses",
    () => accessApi.getAccesses(),
    {
      initialData: [],
    },
  );

  const { isLoading: isLoadingAreas, data: mapAreas } = useCustomQuery(
    "map-areas",
    () => mapAreaApi.getMapAreas(),
  );

  // This is used to generate read/update pairs from accesses
  const generateAccessPair = useCallback(
    (mapAreaRequired: boolean) => {
      const filteredAccesses = accesses?.filter(
        (x) => x.mapAreaRequired === mapAreaRequired,
      );

      return filteredAccesses?.reduce<{
        pairs: AccessPair[];
        rest: AccessItemDto[];
      }>(
        (total, access) => {
          const accessName = AccessEnum[access.accessId];
          if (accessName.endsWith("Read")) {
            const firstPart = accessName.substr(0, accessName.length - 4);
            const pair = accesses?.find(
              (y) => AccessEnum[y.accessId] === `${firstPart}Update`,
            );
            // console.log(firstPart);
            // console.log(pair);
            if (pair) {
              return {
                rest: [
                  ...total.rest.filter((x) => x.accessId !== pair.accessId),
                ],
                pairs: [
                  ...total.pairs,
                  { read: access, update: pair, name: firstPart },
                ],
              };
            }
          }
          if (total.pairs.some((p) => p.update.accessId === access.accessId)) {
            return total;
          }
          return { ...total, rest: [...total.rest, access] };
        },
        { pairs: [], rest: [] },
      );
    },
    [accesses],
  );

  const onCheckboxClickCallback = useCallback(
    (access: AccessItemDto | AccessItemDto[], area?: MapAreaItemDto) => {
      const givenAccesses = "length" in access ? access : [access];
      const f = fields.filter((x: PermissionItemDto) =>
        givenAccesses.find(
          (y) =>
            x.accessId === y.accessId &&
            ((!area && !x.mapAreaId) || x.mapAreaId === area?.mapAreaId),
        ),
      );
      if (f.length > 0) {
        remove(f.map((x) => fields.indexOf(x)));
      } else {
        append(
          givenAccesses.map((x) => ({
            accessId: x.accessId,
            mapAreaId: area ? area.mapAreaId : undefined,
          })),
        );
      }
    },
    [append, fields, remove],
  );

  const isChecked = useCallback(
    (access: AccessItemDto, area?: MapAreaItemDto) =>
      fields.some(
        (x) =>
          x.accessId === access.accessId &&
          ((!area && !x.mapAreaId) || x.mapAreaId === area?.mapAreaId),
      ),
    [fields],
  );

  const renderAccessRow = useCallback(
    (access: AccessItemDto | AccessPair, area?: MapAreaItemDto) => {
      const isPair = "read" in access;
      const name = "read" in access ? access.name : AccessEnum[access.accessId];
      const readPair = "read" in access ? access.read : null;
      const updatePair = "read" in access ? access.update : null;

      return (
        <Box
          key={`${name}-${area?.mapAreaId ?? "no-area"}`}
          sx={{ display: "grid", gridTemplateColumns: "auto 42px 42px" }}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{ display: "flex", alignItems: "center" }}
          >
            {t(isPair ? `access-pair:${name.toLowerCase()}` : `access:${name}`)}
          </Typography>
          <Checkbox
            checked={isChecked((readPair ?? access) as AccessItemDto, area)}
            onChange={() => {
              if (
                readPair &&
                updatePair &&
                isChecked(readPair, area) &&
                isChecked(updatePair, area)
              ) {
                onCheckboxClickCallback([updatePair, readPair], area);
              } else {
                onCheckboxClickCallback(
                  (readPair ?? access) as AccessItemDto,
                  area,
                );
              }
            }}
          />
          {updatePair && (
            <Checkbox
              checked={isChecked(updatePair, area)}
              disabled={!isChecked(readPair!, area)}
              onChange={() => onCheckboxClickCallback(updatePair, area)}
            />
          )}
        </Box>
      );
    },
    [isChecked, onCheckboxClickCallback, t],
  );

  const mappedMapAreas = useMemo(
    () =>
      mapAreas?.results.map((area) => (
        <Box key={area.mapAreaId}>
          <Typography variant="h6">{area.name}</Typography>
          <Box sx={{ pl: 1 }}>
            {generateAccessPair(true)?.pairs.map((pair) =>
              renderAccessRow(pair, area),
            )}
            {generateAccessPair(true)?.rest.map((access) =>
              renderAccessRow(access, area),
            )}
          </Box>
        </Box>
      )),
    [generateAccessPair, mapAreas?.results, renderAccessRow],
  );

  const mappedGlobalPerms = useMemo(
    () => (
      <Box sx={{ pl: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "auto 42px 42px" }}>
          <Typography variant="body1">{t("access")}</Typography>
          <Typography variant="body1">{t("read-access")}</Typography>
          <Typography variant="body1">{t("update-access")}</Typography>
        </Box>
        {generateAccessPair(false)?.pairs.map((pair) => renderAccessRow(pair))}
        {generateAccessPair(false)?.rest.map((access) =>
          renderAccessRow(access),
        )}
      </Box>
    ),
    [generateAccessPair, renderAccessRow, t],
  );

  if (isLoadingAcesses || isLoadingAreas) return <LoadingBox />;

  return (
    <Box sx={{ height: "100%" }}>
      <SmartForm methods={formMethods} onSubmit={onSubmit} noGrid>
        <Box sx={{ maxHeight: "100%" }}>
          <Typography variant="h6">{t("global-permissions")}</Typography>
          {mappedGlobalPerms}
          {mappedMapAreas}
        </Box>
        <SmartSubmit
          disabled={!formMethods.formState.isDirty}
          variant="contained"
        >
          {t("save-permissions")}
        </SmartSubmit>
      </SmartForm>
    </Box>
  );
};
